import axios from 'axios';
import {ENDPOINT_URL, GET_USERS_LIST} from '../../../constants/commonConstants';
import {
	decodeAgentResponse,
	LOOKUP_LIMIT,
	optionalText,
	resolveCandidatesWithFallback,
	responseRows,
} from '../../core/resolutionUtils';
import {
	getResolvedEntity,
	replaceResolvedEntities,
} from '../../core/resolvedEntityStore';

/**
 * Return a Canvas user row identifier.
 * @param {object} item Canvas user row
 * @return {*} identifier
 */
function userId(item) {
	return item.id ?? item.user_id;
}

/**
 * Normalize a Canvas user row.
 *
 * Canvas matches ?search= against the username, the full name and the email
 * server-side, so any of the three can be what the request named. The name
 * carried back is the username, because that is the one guaranteed unique
 * and the one the table shows first.
 *
 * @param {object} item Canvas user row
 * @return {object|null} agent candidate
 */
function normalizeUser(item) {
	const id = userId(item);
	const name = item.username ?? item.user_name;
	if (id == null || !name) return null;
	// Anything beyond the declared candidate shape belongs in field_values,
	// keyed by the catalogue's own field ids - the model forbids extras, so
	// a full name sent as a top-level attribute is rejected outright.
	const values = {};
	if (item.full_name) values.user_full_name = String(item.full_name);
	if (item.email) values.user_email = String(item.email);
	values.user_name = String(name);
	return {
		entity_type: 'user',
		entity_id: String(id),
		name: String(name),
		status: optionalText(item.status),
		site_id: null,
		site_name: null,
		serial_no: null,
		field_values: values,
	};
}

// Generic words that mean "no user was actually named" (e.g. "edit user"
// with nothing left over to search for) rather than a real, if partial,
// username. Left unfiltered, a bare "user" is a valid substring match
// against any username/full name/email literally containing that word -
// a false positive that looks like a resolved target but isn't one the
// person named.
const GENERIC_USER_QUERIES = new Set(['user', 'users', 'a user', 'the user', 'any user']);

/**
 * Resolve Canvas user candidates.
 * @param {object} params resolver parameters
 * @return {Promise<{candidates: object[]}>} normalized candidates
 */
export async function resolveUsers(params) {
	const query = String(params.query || '').trim();
	const browse = params.browse === true;
	if (!query && !browse) throw new Error('lookup_query_required');
	if (GENERIC_USER_QUERIES.has(query.toLowerCase())) {
		throw new Error('no_target_specified');
	}
	const loadRows = async (search, limit) => {
		const searchParam = search
			? `&search=${encodeURIComponent(search)}`
			: '';
		const url = `${ENDPOINT_URL}${GET_USERS_LIST}?limit=${limit}` +
			`&offset=0${searchParam}`;
		const {data} = await axios.get(url);
		return responseRows(decodeAgentResponse(data) || {});
	};
	if (!query) {
		const limit = Math.min(
			Math.max(Number(params.limit) || LOOKUP_LIMIT, 1),
			LOOKUP_LIMIT,
		);
		const rows = await loadRows(null, limit);
		replaceResolvedEntities('user', rows, userId);
		return {
			candidates: rows.map(normalizeUser).filter(Boolean).slice(0, limit),
		};
	}
	const {rows, candidates} = await resolveCandidatesWithFallback({
		query,
		loadRows,
		normalizeRow: normalizeUser,
		fuzzy: params.fuzzy === true,
	});
	replaceResolvedEntities('user', rows, userId);
	return {candidates};
}

/**
 * Return a retained Canvas user row.
 * @param {string} entityId entity identifier
 * @return {object|null} Canvas row
 */
export function getResolvedUser(entityId) {
	return getResolvedEntity('user', entityId);
}
